import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import debounce from "lodash/debounce";
import theme from "../../../../assets/theme";
import {
  Paper,
  Typography,
  Modal,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import { Formik } from "formik";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import { useSnackbar } from "notistack";
import { FormLabel } from "../../NoticeGeneration/styles";
import FormField from "../../../common/FormField/FormField";
import SystemService from "../../../../services/SystemService";
import PartyService from "../../../../services/PartyService";
import queryString from "query-string";
import AuthService from "../../../../services/AuthService";
import { signerGatewayValidationSchema } from "../../../../helpers/validationSchema";
import { s3Upload } from "../../../common/FileUploadS3";
import { getFileName } from "../../../../helpers/functions";
import useLoader from "../../../../hooks/useLoader";
import {
  StyledAutocomplete,
  OptionContainer,
  OptionContent,
  OptionCheck,
  OptionName,
  OptionEmail,
} from "../../NoticeGeneration/component/CustomSelect";
import {
  TabStrip,
  TabButton,
  TabLabel,
  TabSub,
  TabActiveBar,
} from "../../../booking_dashboard/WalkinBooking/Styles";

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
  radioRoot: {
    margin: "0 0 4px 4px",
  },
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
  },
}));

const PreSignIn = ({ signIn, setSignIn, MTRef }) => {
  const { enqueueSnackbar } = useSnackbar();
  const neutralList = [{ label: "None", value: "none" }];
  const [arbitratorOptions, setArbitratorOptions] = useState([]);
  const [arbitratorInputValue, setArbitratorInputValue] = useState("");
  const [arbitratorLoading, setArbitratorLoading] = useState(false);
  const [isArbitratorPrepopulated, setIsArbitratorPrepopulated] =
    useState(false);
  const [selectedArbitrator, setSelectedArbitrator] = useState(null);

  const { setLoader } = useLoader();
  const classes = useStyles();

  const debouncedArbitratorSearch = useMemo(
    () =>
      debounce(async (search) => {
        try {
          setArbitratorLoading(true);

          let neutralParams = {
            page: 1,
            perPage: 20,
            role: "mediator",
            query: search,
          };
          let partyParams = {
            page: 1,
            perPage: 10,
            search,
            status: "",
          };

          const [neutralRes, partyRes] = await Promise.all([
            SystemService.getProfiles(
              `?${queryString.stringify(neutralParams)}`,
            ),
            PartyService.index(`?${queryString.stringify(partyParams)}`),
          ]);

          const neutrals =
            neutralRes?.data?.map((item) => ({
              type: "neutral",
              label: item.name,
              value: item.id,
              email: item.email,
            })) || [];

          const parties =
            partyRes?.data?.map((item) => ({
              type: "party",
              label: item.name,
              value: item.id,
              email: item.owner?.email || "",
            })) || [];

          setArbitratorOptions([...neutrals, ...parties]);
        } catch (error) {
          console.error(error);
        } finally {
          setArbitratorLoading(false);
        }
      }, 400),
    [],
  );

  useEffect(() => {
    if (isArbitratorPrepopulated) {
      if (!arbitratorInputValue || arbitratorInputValue.length < 2) {
        setArbitratorOptions([]);
      }
      return;
    }

    if (arbitratorInputValue.length >= 2) {
      debouncedArbitratorSearch(arbitratorInputValue);
    } else {
      setArbitratorOptions([]);
    }
  }, [
    arbitratorInputValue,
    debouncedArbitratorSearch,
    isArbitratorPrepopulated,
  ]);

  const inputs = [
    {
      label: "Signature Mode",
      name: "SignatureMode",
      type: "select",
      menuItems: [
        { label: "Place signature", value: 3, disabled: false },
        { label: "AADHAAR based document signing", value: 12 },
      ],
    },
    {
      label: "Signature Position",
      name: "SignaturePosition",
      type: "select",
      menuItems: [
        { label: "Top left", value: "Top-Left" },
        { label: "Top center", value: "Top-Center" },
        { label: "Top right", value: "Top-Right" },
        { label: "Middle left", value: "Middle-Left" },
        { label: "Middle right", value: "Middle-Right" },
        { label: "Middle center", value: "Middle-Center" },
        { label: "Bottom left", value: "Bottom-Left" },
        { label: "Bottom right", value: "Bottom-Right" },
        { label: "Bottom center", value: "Bottom-Center" },
        { label: "Customize", value: "Customize" },
      ],
      required: true,
    },
    // This one will be replaced with searchable Autocomplete
    {
      label: "Arbitrator",
      name: "arbitratorName",
      type: "select",
      menuItems: neutralList,
      required: true,
    },
  ];

  const mcx_inputs = [
    {
      label: "Signature Mode",
      name: "SignatureMode",
      type: "select",
      menuItems: [
        { label: "Place signature", value: 3, disabled: false },
        { label: "AADHAAR based document signing", value: 12 },
      ],
    },
    {
      label: "Signature Position",
      name: "SignaturePosition",
      type: "select",
      menuItems: [
        { label: "Top left", value: "Top-Left" },
        { label: "Top center", value: "Top-Center" },
        { label: "Top right", value: "Top-Right" },
        { label: "Middle left", value: "Middle-Left" },
        { label: "Middle right", value: "Middle-Right" },
        { label: "Middle center", value: "Middle-Center" },
        { label: "Bottom left", value: "Bottom-Left" },
        { label: "Bottom right", value: "Bottom-Right" },
        { label: "Bottom center", value: "Bottom-Center" },
        { label: "Customize", value: "Customize" },
      ],
      required: true,
    },
    {
      label: "Name",
      name: "neutralName",
      type: "text",
      required: true,
    },
    {
      label: "Email",
      name: "arbitratorEmailId",
      type: "text",
      required: true,
    },
  ];

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "please wait..." });
      let arbitrator =
        selectedArbitrator ||
        arbitratorOptions.find(
          ({ value }) => value === values?.arbitratorName,
        ) ||
        neutralList.find(({ value }) => value === values?.arbitratorName);

      const payload = {
        arbitratorName: values?.neutralName
          ? values?.neutralName
          : arbitrator?.label || "None",
        SignatureMode: values?.SignatureMode,
        SignaturePosition: values.SignaturePosition,
        arbitratorId: values.arbitratorName || 1,
        arbitratorEmailId: values?.arbitratorEmailId
          ? values?.arbitratorEmailId
          : arbitrator?.email || "",
        arbitratorType: arbitrator?.type || "neutral",
        CustomizeCoordinates: values?.CustomizeCoordinates,
        internalReferenceNumber: values?.internalReferenceNumber,
        fileBase64: values.file,
        typeOfSelection: values.typeOfSelection,
        SelectPage: values.SelectPage || "ALL",
        PageNumber: values.PageNumber || 1,
      };
      const response = await AuthService.createEsign(payload);
      if (response.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Something went wrong", {
        variant: "error",
      });
    } finally {
      setSignIn({ state: false });
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
    }
  }

  return (
    <Modal
      open={signIn?.state}
      onClose={() => setSignIn({ state: false })}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container elevation={0}>
          <HeadingContainer>
            <Heading>Signer Gateway</Heading>
          </HeadingContainer>
          <Formik
            initialValues={{
              Name: "",
              fileName: "",
              SignatureMode: 12,
              SignaturePosition: "",
              arbitratorName: "",
              file: "",
              CustomizeCoordinates: "",
              typeOfAction: 1,
              arbitratorEmailId: "",
              neutralName: "",
              internalReferenceNumber: "",
              typeOfSelection: "neutral",
              SelectPage: "ALL",
              PageNumber: 1,
            }}
            enableReinitialize
            validateOnBlur
            validationSchema={signerGatewayValidationSchema}
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              handleSubmit,
              touched,
              setFieldValue,
              handleBlur,
              isValid,
            }) => (
              <>
                <>
                  <ScrollBody>
                    <FieldBlock>
                      <TabStrip>
                        {[
                          { label: "JustAct Team", value: 1 },
                          { label: "Others", value: 0 },
                        ].map((m) => {
                          const active = values.typeOfAction === m.value;
                          return (
                            <TabButton
                              key={m.value}
                              type="button"
                              $active={active}
                              onClick={() =>
                                setFieldValue("typeOfAction", m.value)
                              }
                            >
                              <TabLabel $active={active}>{m.label}</TabLabel>
                              <TabSub $active={active}>{m.desc}</TabSub>
                              <TabActiveBar $active={active} />
                            </TabButton>
                          );
                        })}
                      </TabStrip>
                    </FieldBlock>

                    {values.typeOfAction === 0 && (
                      <FieldBlock>
                        <RadioGroup
                          className={classes.radioRoot}
                          style={{ flexDirection: "row" }}
                          name={"typeOfSelection"}
                          value={values.typeOfSelection}
                          onChange={(e) => {
                            setFieldValue("typeOfSelection", e.target.value);
                          }}
                        >
                          {[
                            { label: "Arbitrators", value: "neutral" },
                            { label: "Users", value: "users" },
                          ].map((radio, key) => (
                            <FormControlLabel
                              key={key}
                              classes={{ label: classes.formLabel }}
                              value={radio.value}
                              control={
                                <Radio
                                  size="medium"
                                  style={{ color: "#00838c" }}
                                />
                              }
                              label={radio.label}
                            />
                          ))}
                        </RadioGroup>
                      </FieldBlock>
                    )}

                    <FieldGrid>
                      {(values.typeOfAction ? inputs : mcx_inputs)
                        .filter((input) =>
                          values.typeOfAction
                            ? input.name !== "arbitratorName"
                            : true,
                        )
                        .map((input, index) => (
                          <FieldBlock key={index}>
                            <FormField
                              menuItems={input?.menuItems}
                              {...{
                                touched,
                                errors,
                                input,
                                values,
                                handleChange,
                                handleBlur,
                                setFieldValue,
                              }}
                            />
                          </FieldBlock>
                        ))}

                      {values.typeOfAction === 1 && (
                        <FieldBlock>
                          <FormLabel>
                            Arbitrator <span className="required-star"> *</span>
                          </FormLabel>
                          <StyledAutocomplete
                            classes={{
                              listbox: classes.listbox,
                              noOptions: classes.noOptions,
                              paper: classes.paper,
                            }}
                            options={arbitratorOptions}
                            loading={arbitratorLoading}
                            inputValue={arbitratorInputValue}
                            onInputChange={(e, val, reason) => {
                              setArbitratorInputValue(val);
                              // Only real user keystrokes should mark the
                              // field as "not prepopulated" (which is what
                              // triggers a fresh search in the effect
                              // below). MUI also fires this on "reset"
                              // (after picking an option) and "clear" — we
                              // don't want those to kick off a redundant
                              // search that can wipe out the selection.
                              if (reason === "input") {
                                setIsArbitratorPrepopulated(false);
                              }
                            }}
                            getOptionLabel={(option) =>
                              option.email
                                ? `${option.label} (${option.email})`
                                : option.label || ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            // Derived from the dedicated selectedArbitrator
                            // state, NOT from arbitratorOptions — the
                            // search results list gets replaced on every
                            // keystroke/search and won't reliably contain
                            // the previously selected item.
                            value={selectedArbitrator}
                            onChange={(event, newValue) => {
                              setFieldValue(
                                "arbitratorName",
                                newValue ? newValue.value : "",
                              );
                              setSelectedArbitrator(newValue || null);
                              if (newValue) {
                                setArbitratorInputValue(newValue.label);
                                setIsArbitratorPrepopulated(true);
                              } else {
                                setArbitratorInputValue("");
                                setIsArbitratorPrepopulated(false);
                              }
                            }}
                            noOptionsText={
                              arbitratorInputValue.length < 2
                                ? "Type at least 2 characters"
                                : arbitratorLoading
                                  ? "Searching..."
                                  : "No neutrals or parties found"
                            }
                            renderInput={(params) => (
                              <CustomInputField
                                {...params}
                                variant="outlined"
                                placeholder="Search neutral or party..."
                                style={{ marginTop: 6 }}
                                error={
                                  touched.arbitratorName &&
                                  Boolean(errors.arbitratorName)
                                }
                                helperText={
                                  touched.arbitratorName &&
                                  errors.arbitratorName
                                }
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {arbitratorLoading && (
                                        <CircularProgress size={18} />
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            PaperProps={{
                              style: {
                                borderRadius: 8,
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            ListboxProps={{
                              style: {
                                fontFamily: "OpenSans-Regular, sans-serif",
                                fontSize: "14px",
                                color: "#293461",
                                padding: "4px 0",
                              },
                            }}
                            renderOption={(option) => (
                              <OptionContainer>
                                <OptionContent
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <OptionName>
                                    {option.label}{" "}
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontFamily:
                                          "openSans-SemiBold, sans-serif",
                                        color:
                                          option.type === "party"
                                            ? "#c2410c"
                                            : "#00838c",
                                      }}
                                    >
                                      (
                                      {option.type === "party"
                                        ? "Party"
                                        : "Neutral"}
                                      )
                                    </span>
                                  </OptionName>
                                  <OptionEmail>{option.email}</OptionEmail>
                                </OptionContent>
                                <OptionCheck>✓</OptionCheck>
                              </OptionContainer>
                            )}
                          />
                        </FieldBlock>
                      )}

                      <FieldBlock>
                        <FormLabel>
                          Uploaded File{" "}
                          <span className="required-star"> *</span>
                        </FormLabel>
                        <CustomFileUpload
                          error={touched["file"] && errors["file"]}
                          setFile={(file) => {
                            if (!file) return;

                            const MAX_SIZE = 10 * 1024 * 1024;

                            if (file.size > MAX_SIZE) {
                              enqueueSnackbar(
                                "File size must be 10 MB or less",
                                {
                                  variant: "error",
                                },
                              );
                              return;
                            }

                            s3Upload(
                              file,
                              "file",
                              setFieldValue,
                              ["application/pdf"],
                              setLoader,
                            );
                          }}
                          width={"100%"}
                          accept={".pdf"}
                          file={{ name: getFileName(values["file"]) }}
                        />
                      </FieldBlock>

                      {[
                        {
                          label: "Internal reference text",
                          name: "internalReferenceNumber",
                          type: "text",
                          required: false,
                        },
                      ].map((input, index) => (
                        <FieldBlock key={index}>
                          <FormField
                            {...{
                              touched,
                              errors,
                              input,
                              values,
                              handleChange,
                              handleBlur,
                              setFieldValue,
                            }}
                          />
                        </FieldBlock>
                      ))}
                      {[
                        {
                          label: "Select Page",
                          name: "SelectPage",
                          type: "select",
                          required: false,
                        },
                      ].map((input, index) => (
                        <FieldBlock key={index}>
                          <FormField
                            menuItems={[
                              { label: "ALL", value: "ALL" },
                              { label: "FIRST", value: "FIRST" },
                              { label: "EVEN", value: "EVEN" },
                              { label: "LAST", value: "LAST" },
                              {
                                label: "ODD",
                                value: "ODD",
                              },
                              { label: "SPECIFY", value: "SPECIFY" },
                              {
                                label: "PAGE LEVEL",
                                value: "PAGE LEVEL",
                              },
                            ]}
                            {...{
                              touched,
                              errors,
                              input,
                              values,
                              handleChange,
                              handleBlur,
                              setFieldValue,
                            }}
                          />
                        </FieldBlock>
                      ))}
                      {values.SelectPage === "SPECIFY" &&
                        [
                          {
                            label: "Page Number",
                            name: "PageNumber",
                            type: "text",
                            placeholder: "1,2,3 or 1-3 (for multiple pages)",
                            required: false,
                          },
                        ].map((input, index) => (
                          <FieldBlock key={index}>
                            <FormField
                              {...{
                                touched,
                                errors,
                                input,
                                values,
                                handleChange,
                                handleBlur,
                                setFieldValue,
                              }}
                            />
                          </FieldBlock>
                        ))}
                      {[
                        {
                          label: "Customize Coordinates",
                          name: "CustomizeCoordinates",
                          type: "text",
                          placeholder: "7,10,60,120 (Left,Top,Width,Height)",
                          discription:
                            "Please refer this link to get the Coordinates",
                          link: "https://coordinates-int.emsigner.com/",
                        },
                      ].map((input, index) => (
                        <FieldBlock key={index}>
                          <FormField
                            {...{
                              touched,
                              errors,
                              input,
                              values,
                              handleChange,
                              handleBlur,
                              setFieldValue,
                            }}
                          />
                        </FieldBlock>
                      ))}
                    </FieldGrid>
                  </ScrollBody>

                  <FormContainer>
                    <ButtonRow>
                      <PrimaryOutlinedCloseCTAButton
                        onClick={() => setSignIn({ state: false })}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      <PrimaryCTAButton
                        onClick={handleSubmit}
                        disabled={!isValid}
                      >
                        Submit
                      </PrimaryCTAButton>
                    </ButtonRow>
                  </FormContainer>
                </>
              </>
            )}
          </Formik>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default PreSignIn;

const DrawerContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  outline: none;

  @media (max-width: 640px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const Container = styled(Paper)`
  && {
    width: 100%;
    max-width: 640px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 45px rgba(20, 40, 70, 0.18);

    @media (max-width: 640px) {
      max-height: 92vh;
      border-radius: 16px 16px 0 0;
    }
  }
`;

const HeadingContainer = styled.div`
  padding: 20px 24px 16px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 16px 16px 12px;
  }
`;

const Heading = styled(Typography)(() => ({
  color: "#293461",
  padding: "14px 16px",
  fontFamily: theme?.fonts?.primaryFontBold || "openSans-Bold, sans-serif",
  fontSize: "17px",
  borderRadius: "10px",
  textAlign: "center",
  letterSpacing: "0.2px",
}));

const ScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 24px 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(41, 52, 97, 0.25);
    border-radius: 3px;
  }

  @media (max-width: 640px) {
    padding: 4px 16px 8px;
  }
`;

const FieldGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldBlock = styled.div`
  width: 100%;
`;

const FormContainer = styled.div`
  flex-shrink: 0;
  padding: 16px 24px 20px;
  border-top: 1px solid #eef0f4;
  background: #ffffff;

  @media (max-width: 640px) {
    padding: 14px 16px 18px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;

  & > button {
    flex: 1;
    padding: 12px 16px;
    border-radius: 10px;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;
